import React, { useMemo, useState } from "react";

import { useGetProductTemplateInfos } from "api/api-hooks/product-templates-hooks";
import { InputChangeEvent } from "api/model/airflow-task/task-template/TaskTemplateInfo";
import { ProductTemplateTaskSelectionOptions } from "api/model/product-template/ProductTemplateTaskSelectionOptions";
import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { ProductTemplateDropdown } from "components/basic/dropdowns/product-template/ProductTemplateDropdown";
import ShortText from "components/basic/short-text/ShortText";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Column, ColumnBodyOptions, ColumnEditorOptions } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import { DataTable, DataTableRowEditCompleteEvent, DataTableRowEditEvent } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { v4 } from "uuid";

interface ITaskConfigProductTemplatesTableProps {
  header: string;
  productTemplates: Record<number, ProductTemplateTaskSelectionOptions>;
  onChange: (e: InputChangeEvent<Record<number, ProductTemplateTaskSelectionOptions>>) => void;
  bodyClass?: string;
}

interface IRowModelValues extends ProductTemplateTaskSelectionOptions {
  prodTemplateId: number | null;
}

interface IProductTemplateRowModel {
  value: IRowModelValues | null;
  newValue: IRowModelValues | null;
  id: string;
}

const defaultRowValues: IRowModelValues = {
  prodTemplateId: null,
  entryDataset: null,
  latestEntryCount: 10,
  useDataset: false,
};

export default function TaskConfigProductTemplatesTable(props: ITaskConfigProductTemplatesTableProps) {
  const { data, isLoading } = useGetProductTemplateInfos();

  const prodTemplateTitleMapif = useMemo(() => {
    const titleMap: Record<number, string> = {};
    data?.forEach(it => {
      titleMap[it.id!!] = it.displayName;
    });
    return titleMap;
  }, [data]);

  if (isLoading) return <i className="pi pi-spin pi-spinner" />;

  return <TaskConfigProductTemplatesTableWrapped {...props} prodTemplateTitleMap={prodTemplateTitleMapif} />;
}

function TaskConfigProductTemplatesTableWrapped(
  props: ITaskConfigProductTemplatesTableProps & { prodTemplateTitleMap: Record<number, string> }
) {
  const [dataArr, setDataArr] = useState<IProductTemplateRowModel[]>(() => mapToList(props.productTemplates));
  const [editingRows, setEditingRows] = useState<Record<string, boolean>>({});

  const propagateChangesStateUp = (arr: IProductTemplateRowModel[]) => {
    const tmp: Record<number, ProductTemplateTaskSelectionOptions> = {};

    arr.forEach(it => {
      if (it.value && it.value?.prodTemplateId !== null) {
        // Template id must be defined
        tmp[it.value.prodTemplateId] = {
          latestEntryCount: it.value.latestEntryCount,
          entryDataset: it.value.entryDataset,
          useDataset: it.value.useDataset,
          filterQuery: it.value.filterQuery,
        };
      }
    });

    props.onChange({ property: "productTemplates", value: tmp } as InputChangeEvent<
      Record<number, ProductTemplateTaskSelectionOptions>
    >);
  };

  const confirmWrapper = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    confirmHandler: () => void
  ) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-dialog.are-you-really-want-to-delete-this-product-template-section"),
      icon: "pi pi-exclamation-triangle",
      accept: confirmHandler,
    });
  };

  const showWarn = (warn: string) => {
    easasAlert({ summary: T("word.invalid"), severity: "warn", detail: warn, life: 3000 });
  };

  const handleAdd = () => {
    const newRow = {
      value: null,
      newValue: { ...defaultRowValues },
      id: v4(),
    } as IProductTemplateRowModel;
    setDataArr(prev => [...prev, newRow]);
    setEditingRows(prev => ({ ...prev, [newRow.id]: true }));
  };

  const handleDelete = (index: number) => {
    setDataArr(prev => {
      const filtered = prev.toSpliced(index, 1);
      propagateChangesStateUp(filtered);
      return filtered;
    });
  };

  const headerTemplate = (
    <div className="flex justify-content-between">
      <h4>{props.header}</h4>
      <Button icon="pi pi-plus" className="p-button-success" onClick={handleAdd} />
    </div>
  );

  const productTemplateEditor = (opt: ColumnEditorOptions) => (
    <ProductTemplateDropdown
      value={opt.rowData.newValue?.prodTemplateId}
      onChange={e => opt.editorCallback?.(e.value)}
    />
  );

  const datasetUseTemplate = (rowData: IProductTemplateRowModel) =>
    rowData.value?.useDataset && <i className="pi pi-check-circle text-green-600" />;

  const datasetUseEditorTemplate = (opt: ColumnEditorOptions) => (
    <Checkbox checked={opt.rowData.newValue?.useDataset} onChange={e => opt.editorCallback?.(e.checked)} />
  );

  const datasetTemplate = (rowData: IProductTemplateRowModel) =>
    rowData.value?.useDataset ? <DatasetTitle identifier={rowData.value.entryDataset} /> : null;

  const datasetEditorTemplate = (opt: ColumnEditorOptions) => (
    <DatasetDropdown value={opt.rowData.newValue.entryDataset} onChange={e => opt.editorCallback?.(e)} />
  );

  const entryCountTemplate = (opt: ColumnEditorOptions) => (
    <InputNumber
      placeholder={T("component.entry-count")}
      value={opt.rowData.newValue.latestEntryCount}
      onChange={e => opt.editorCallback?.(e.value)}
    />
  );

  const filterQueryTemplate = (rowData: IProductTemplateRowModel) => (
    <ShortText text={rowData.value?.filterQuery} />
  );

  const filterEditorTemplate = (opt: ColumnEditorOptions) => (
    <InputText
      placeholder={T("word.filter")}
      value={opt.rowData.newValue.filterQuery}
      onChange={e => opt.editorCallback?.(e.target.value)}
    />
  );

  const renderDeleteButton = (_: IProductTemplateRowModel, options: ColumnBodyOptions) => {
    return (
      <Button
        icon="pi pi-trash"
        className="const-del-btn  p-button-rounded p-button-text p-button-danger"
        onClick={e => confirmWrapper(e, () => handleDelete(options.rowIndex))}
      />
    );
  };

  const handleRowEditInit = (e: DataTableRowEditEvent) => {
    const data = e.data as IProductTemplateRowModel;
    data.newValue = { ...data.value! };
  };

  const handleRowEditCancel = (e: DataTableRowEditEvent) => {
    const data = e.data as IProductTemplateRowModel;
    if (data?.newValue?.prodTemplateId === null) {
      setDataArr(prev => prev.filter(item => item.id !== data.id));
      setEditingRows(prev => ({ ...prev, [data.id]: false }));
    }
    data.newValue = null;
  };

  const handleRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const newData = e.newData as IProductTemplateRowModel;
    if (
      newData?.newValue?.prodTemplateId === null ||
      (newData?.newValue?.useDataset && newData?.newValue?.entryDataset == null)
    ) {
      return;
    }

    setDataArr(prev => {
      const updated = prev.map((item, index) =>
        index === e.index ? { ...item, value: newData.newValue } : item
      );
      propagateChangesStateUp(updated);
      return updated;
    });
  };

  const onRowEditChange = (e: DataTableRowEditEvent) => {
    const newData = dataArr.at(e.index);
    const exist = e.data[newData!.id];
    if (newData?.newValue?.prodTemplateId === null) {
      if (!exist) {
        showWarn(T("alert.product-template-must-be-selected"));
        return;
      }
    }

    if (newData?.newValue?.useDataset && newData?.newValue?.entryDataset == null) {
      if (!exist) {
        showWarn(T("alert.dataset-must-be-selected"));
        return;
      }
    }
    setEditingRows(e.data);
  };

  return (
    <DataTable
      value={dataArr}
      dataKey="id"
      editMode="row"
      onRowEditInit={handleRowEditInit}
      onRowEditCancel={handleRowEditCancel}
      onRowEditComplete={handleRowEditComplete}
      onRowEditChange={onRowEditChange}
      editingRows={editingRows}
      header={headerTemplate}
      width="100%"
      className={props.bodyClass}
      size="small"
    >
      <Column
        field="newValue.prodTemplateId"
        header={T("load-conf.product-template")}
        editor={productTemplateEditor}
        body={rowData => props.prodTemplateTitleMap[rowData.value?.prodTemplateId!!]}
        style={{ width: "20%" }}
      />
      <Column
        field="newValue.latestEntryCount"
        header={T("component.entry-count")}
        editor={entryCountTemplate}
        body={rowData => rowData.value?.latestEntryCount}
        style={{ width: "10%" }}
      />
      <Column
        field="newValue.filterQuery"
        header={T("word.filter")}
        editor={filterEditorTemplate}
        body={filterQueryTemplate}
        style={{ width: "20%" }}
      />
      <Column
        field="newValue.useDataset"
        header={T("action.use-dataset")}
        editor={datasetUseEditorTemplate}
        body={datasetUseTemplate}
        style={{ width: "10%" }}
      />
      <Column
        field="newValue.entryDataset"
        header={T("component.entries-dataset")}
        editor={datasetEditorTemplate}
        body={datasetTemplate}
      />
      <Column rowEditor align="right" className="w-5rem p-0" />
      <Column body={renderDeleteButton} className="w-3rem p-0" />
    </DataTable>
  );
}

function mapToList(map: Record<number, ProductTemplateTaskSelectionOptions>): IProductTemplateRowModel[] {
  return Object.keys(map).map(k => {
    const id = parseInt(k, 10);
    const rowValues = { ...map[id], prodTemplateId: id } as IRowModelValues;
    return { value: rowValues, id: v4() } as IProductTemplateRowModel;
  });
}
